import React, { Suspense, lazy } from 'react';
import { Navigate, useLocation, useRoutes } from 'react-router-dom';
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import USER_ROLES from '../constants/roles';

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = useLocation();
  return (
    <Suspense
      key={location.key}
      // fallback={<div className="text-black">Loading...</div>}
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    // Auth Routes
    {
      children: [
        { element: <LoginPage />, index: true },
        {
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
          path: 'login',
        },
        {
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
          path: 'register',
        },
      ],
      path: 'auth',
      element: <Layout noPadding={true} />,
    },
    // Main Routes
    {
      children: [
        { element: <NotFound />, path: '404' },
        {
          element: <MainHome />,
          index: true,
        },
        {
          element: (
            <AuthGuard>
              <Whishlist />
            </AuthGuard>
          ),
          path: 'whishlist',
        },
        // {
        //   path: 'profile',
        //   children: [
        //     { index: true, element: <Profile /> },
        //     { path: 'order', element: <OrderProfile /> },
        //   ],
        // },
        {
          element: <LivePage />,
          path: 'lives',
        },
        {
          element: <BrandPage />,
          path: 'brands',
        },
        {
          element: <CategoryPage />,
          path: 'categories',
        },
      ],
      element: <Layout />,
      path: '',
    },
    {
      children: [
        {
          element: <Cart />,
          index: true,
        },
        {
          path: 'shipping',
          element: (
            <AuthGuard>
              <ShippingForm />
            </AuthGuard>
          ),
        },
      ],
      path: 'cart',
      element: <Layout noPadding={true} />,
    },
    {
      children: [
        {
          element: <Product />,
          path: ':id',
        },
      ],
      path: 'live',
    },
    {
      children: [
        {
          element: <Product />,
          path: ':id',
        },
      ],
      path: 'promo',
    },
    {
      children: [
        {
          element: <Category />,
          path: ':id',
        },
      ],
      path: 'by-type',
    },
    {
      children: [
        {
          element: <Category />,
          path: ':id',
        },
      ],
      path: 'category',
    },
    {
      children: [
        {
          element: <Product />,
          path: ':id',
        },
      ],
      path: 'product',
    },
    {
      children: [
        {
          path: 'admin',
          children: [
            {
              path: 'dashboard',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <Admin />
                </AuthGuard>
              ),
            },
            {
              path: 'orders',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminOrders />
                </AuthGuard>
              ),
            },
            {
              path: 'users',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminUsers />
                </AuthGuard>
              ),
            },
            {
              path: 'products',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminProducts />
                </AuthGuard>
              ),
            },
            {
              path: 'add-product',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminAddProduct />
                </AuthGuard>
              ),
            },
            {
              path: 'edit-product/:id',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminEditProduct />
                </AuthGuard>
              ),
            },
            {
              path: 'categories',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminCategories />
                </AuthGuard>
              ),
            },
            {
              path: 'promo-codes',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminPromoCodeList />
                </AuthGuard>
              ),
            },
            {
              path: 'promo-codes/new',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminPromoCodeForm />
                </AuthGuard>
              ),
            },
            {
              path: 'promo-codes/:id',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminPromoCodeDetails />
                </AuthGuard>
              ),
            },
            {
              path: 'promo-codes/:id/edit',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminPromoCodeForm />
                </AuthGuard>
              ),
            },
            {
              path: 'orders/:orderId/status',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <AdminOrderStatusManagement />
                </AuthGuard>
              ),
            },
            // { path: 'settings', element: <AdminSettings /> },
          ],
        },
      ],
      path: '',
      element: <AdminLayout noPadding={true} />,
    },
    {
      children: [
        {
          path: 'profile',
          children: [
            {
              index: true,
              element: (
                <AuthGuard requiredRole={[USER_ROLES.USER]}>
                  <Profile />
                </AuthGuard>
              ),
            },
            {
              path: 'order',
              element: (
                <AuthGuard requiredRole={[USER_ROLES.USER]}>
                  <OrderProfile />
                </AuthGuard>
              ),
            },
            {
              path: 'order-admin',
              element: (
                <AuthGuard
                  requiredRole={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}
                >
                  <OrderAdminProfile />
                </AuthGuard>
              ),
            },
          ],
        },
        {
          path: 'orders',
          element: (
            <AuthGuard requiredRole={[USER_ROLES.USER]}>
              <Orders />
            </AuthGuard>
          ),
        },
        {
          path: 'shipping-info',
          element: (
            <AuthGuard requiredRole={[USER_ROLES.USER]}>
              <ShippingInfoPage />
            </AuthGuard>
          ),
        },
        {
          path: 'settings',
          element: (
            <AuthGuard requiredRole={[USER_ROLES.USER]}>
              <SettingPage />
            </AuthGuard>
          ),
        },
      ],
      path: '',
      element: <UserLayout noPadding={true} />,
    },
    {
      path: 'mg_succes',
      element: <Layout noPadding={true} />,
      children: [{ element: <MonegasySuccess />, index: true }],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

const MainHome = Loadable(lazy(() => import('../pages/home/index')));
const Whishlist = Loadable(lazy(() => import('../pages/whishlist/index')));
const LoginPage = Loadable(lazy(() => import('../pages/auth/index')));
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Profile = Loadable(lazy(() => import('../pages/auth/Profile')));
const OrderProfile = Loadable(lazy(() => import('../pages/auth/OrderProfile')));
const ShippingInfoPage = Loadable(
  lazy(() => import('../pages/home/ShippingInfoPage'))
);
const SettingPage = Loadable(lazy(() => import('../pages/home/SettingPage')));
const OrderAdminProfile = Loadable(
  lazy(() => import('../pages/auth/AdminOrder'))
);
const Orders = Loadable(lazy(() => import('../pages/auth/Orders')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const Cart = Loadable(lazy(() => import('../pages/home/cart/index')));
const Product = Loadable(lazy(() => import('../pages/home/product/index')));
const LivePage = Loadable(lazy(() => import('../pages/home/product/LivePage')));
const BrandPage = Loadable(lazy(() => import('../pages/home/brands/index')));
const CategoryPage = Loadable(
  lazy(() => import('../pages/home/categories/index'))
);
// const ByType = Loadable(lazy(() => import('../pages/home/product/ByType')));
const Category = Loadable(
  lazy(() => import('../pages/home/product/FilterByCategory'))
);
const ShippingForm = Loadable(
  lazy(() => import('../pages/home/cart/ShippingForm'))
);
const MonegasySuccess = Loadable(
  lazy(() => import('../pages/home/MonegasySuccess'))
);
const Layout = Loadable(lazy(() => import('../layouts/main/Layout')));
const NotFound = Loadable(lazy(() => import('../pages/404/Page404')));

// Admin Routes
const AdminLayout = Loadable(
  lazy(() => import('../layouts/main/admin/AdminLayout'))
);
const UserLayout = Loadable(
  lazy(() => import('../layouts/main/user/UserLayout'))
);
const Admin = Loadable(lazy(() => import('../pages/auth/Admin')));
const AdminOrders = Loadable(lazy(() => import('../pages/admin/Orders')));
const AdminUsers = Loadable(lazy(() => import('../pages/admin/Users')));
const AdminProducts = Loadable(lazy(() => import('../pages/admin/Products')));
const AdminAddProduct = Loadable(
  lazy(() => import('../pages/admin/AddProduct'))
);
const AdminEditProduct = Loadable(
  lazy(() => import('../pages/admin/EditProduct'))
);
const AdminCategories = Loadable(
  lazy(() => import('../pages/admin/Categories'))
);

// Admin Promo Code Routes
const AdminPromoCodeList = Loadable(
  lazy(() => import('../pages/admin/PromoCodeList'))
);
const AdminPromoCodeForm = Loadable(
  lazy(() => import('../pages/admin/PromoCodeForm'))
);
const AdminPromoCodeDetails = Loadable(
  lazy(() => import('../pages/admin/PromoCodeDetails'))
);
const AdminOrderStatusManagement = Loadable(
  lazy(() => import('../pages/admin/OrderStatusManagement'))
);

// const AdminSettings = Loadable(lazy(() => import('../pages/admin/Settings')));
